import { useMemo } from 'react'
import { Alert, Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import { useOpenApiOverview } from '../../../docs/openapi/useOpenApiResources'
import { getWorkspacePage, subPagePath } from '../../../navigation'
import { DocBlocks } from '../DocBlocks'
import { DocLayout } from '../DocLayout'
import { docSectionId, useDocSectionScroll } from '../useDocSectionScroll'
import styles from '../Docs.module.scss'

const overviewPath = (slug: string) => subPagePath('api-overview', slug)

/**
 * API Overview: the overview the backend publishes with the organisation's
 * OpenAPI document (`info.description`: authentication, standard responses,
 * error codes, pagination, rate limiting, …), every section on one
 * continuous page. The `:sectionId` route names the section in view, like the
 * other documentation pages (`useDocSectionScroll`). The sidebar's section
 * list comes from the bundled snapshot; the content here is live.
 */
export function ApiOverviewPage() {
  const { t } = useTranslation()
  const { sectionId } = useParams<{ sectionId: string }>()
  const page = getWorkspacePage('api-overview')
  const { sections, source, isPending, refetch } = useOpenApiOverview()
  const slugs = useMemo(() => sections.map(section => section.id), [sections])
  const active = sections.find(section => section.id === sectionId)

  useDocSectionScroll(active?.id, slugs, overviewPath)

  if (!isPending && sections.length > 0 && !active) {
    return <Navigate to={overviewPath(sections[0].id)} replace />
  }
  if (!sectionId && page.children?.length) {
    return <Navigate to={subPagePath(page, page.children[0])} replace />
  }

  return (
    <div className={styles.previewRoot} data-testid="dp-doc-overview">
      {source === 'snapshot' && (
        <Alert
          className={styles.previewBanner}
          type="warning"
          showIcon
          message={t('developerPlatform.docs.overview.bannerSnapshot')}
          action={
            <Button size="small" onClick={refetch} data-testid="dp-doc-overview-refetch">
              {t('developerPlatform.docs.openapiPreview.refetch')}
            </Button>
          }
        />
      )}

      {isPending && (
        <div
          className={styles.previewLoading}
          aria-busy="true"
          data-testid="dp-doc-overview-loading"
        >
          <span />
          <span />
          <span />
        </div>
      )}

      {!isPending && sections.length === 0 && (
        <Alert type="warning" showIcon message={t('developerPlatform.docs.overview.empty')} />
      )}

      <div className={styles.docStack}>
        {sections.map(section => (
          <DocLayout
            key={section.id}
            id={docSectionId(section.id)}
            title={section.title}
            main={<DocBlocks blocks={section.main} />}
            aside={section.aside ? <DocBlocks blocks={section.aside} /> : undefined}
            data-testid={`dp-doc-overview-${section.id}`}
          />
        ))}
      </div>
    </div>
  )
}
